import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { AlertTriangle, BellRing, CalendarClock, Car, UserCheck, ExternalLink, CheckCircle2, History, Play, Mail } from "lucide-react";
import { toast } from "sonner";

const SEV = {
  scaduto: { label: "Scaduto", cls: "border-rose-500/50 text-rose-300 bg-rose-500/10" },
  imminente: { label: "Scadenza imminente", cls: "border-amber-400/60 text-amber-200 bg-amber-500/10" },
  in_scadenza: { label: "In scadenza", cls: "border-yellow-600/40 text-yellow-200/90 bg-yellow-500/5" },
};
const fmtD = (iso) => iso ? iso.split("-").reverse().join("/") : "—";
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const daysLabel = (d) => d < 0 ? `Scaduto da ${-d} g` : d === 0 ? "Scade oggi" : `Tra ${d} g`;

function Counter({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-b from-[#121009] to-[#070605] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400"><Icon size={14} className={tone} />{label}</div>
      <div className={`font-display text-3xl mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function NotifStatus({ it }) {
  if (!it.notifications_count) return <span className="text-xs text-slate-500">Nessun avviso ancora inviato</span>;
  return (
    <div className="text-xs text-slate-400 space-y-0.5" data-testid={`alert-notif-status-${it.alert_id}`}>
      <div>{it.notifications_count} avvis{it.notifications_count === 1 ? "o" : "i"} · ultimo {fmtDT(it.last_notified_at)}</div>
      <div className="flex items-center gap-1"><Mail size={11} />
        <span className="text-emerald-300">{it.emails_sent} email inviate</span>
        {it.emails_failed > 0 && <span className="text-rose-300">· {it.emails_failed} non consegnate</span>}
        {it.emails_deferred > 0 && <span className="text-amber-300">· {it.emails_deferred} in attesa di reinvio</span>}
      </div>
    </div>
  );
}

function AlertRow({ it, onOpen, onAck, onHistory }) {
  const Icon = it.kind === "vehicle" ? Car : UserCheck;
  return (
    <div className="rounded-2xl border border-slate-800 bg-[#0C0B09] p-4 grid gap-3 md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-center" data-testid={`expiry-alert-${it.alert_id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-white font-semibold"><Icon size={15} className="text-amber-400 shrink-0" /><span className="truncate">{it.doc_type}</span></div>
        <div className="text-sm text-slate-300 truncate">{it.entity_name}{it.plate ? ` · ${it.plate}` : ""}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${SEV[it.severity].cls}`} data-testid="alert-severity">{SEV[it.severity].label}</span>
        <span className="text-sm text-slate-200 font-mono-num">{fmtD(it.expiry_date)}</span>
        <span className="text-xs text-slate-400" data-testid="alert-days">{daysLabel(it.days)}</span>
        {it.alert_status === "acknowledged" && <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-sky-500/40 text-sky-300" title={`Da ${it.acknowledged_by || ""}`} data-testid="alert-ack-badge">Preso in carico</span>}
      </div>
      <NotifStatus it={it} />
      <div className="flex gap-2 md:justify-end">
        <button type="button" onClick={() => onOpen(it)} data-testid={`alert-open-${it.alert_id}`} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold bg-gradient-to-b from-[#F1D27A] to-[#C99A24] text-black"><ExternalLink size={13} />Apri scheda</button>
        {it.alert_status === "open" && <button type="button" onClick={() => onAck(it)} data-testid={`alert-ack-${it.alert_id}`} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-slate-300 hover:border-amber-400"><CheckCircle2 size={13} />Prendi in carico</button>}
        <button type="button" onClick={() => onHistory(it)} data-testid={`alert-history-${it.alert_id}`} className="rounded-lg px-2 py-1.5 text-slate-400 border border-slate-800 hover:text-amber-300" title="Storico"><History size={14} /></button>
      </div>
    </div>
  );
}

function HistoryDialog({ alertId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/expiry-alerts/${alertId}/history`).then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
  }, [alertId]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0C0B09] border-amber-500/30" data-testid="alert-history-dialog">
        <DialogHeader><DialogTitle className="text-white">Storico notifiche</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-slate-500">Caricamento...</div> : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto text-sm">
            {data.events.length === 0 && <div className="text-slate-500">Nessun invio registrato.</div>}
            {data.events.map(e => (
              <div key={e.id} className="rounded-lg border border-slate-800 p-2.5" data-testid={`history-event-${e.id}`}>
                <div className="flex justify-between gap-2"><span className="text-slate-200">{e.channel === "email" ? `Email → ${e.recipient}` : "Notifica interna"}</span>
                  <span className={e.status === "sent" ? "text-emerald-300" : e.status === "error" ? "text-rose-300" : "text-slate-400"}>{e.status === "sent" ? "Inviata" : e.status === "error" ? "Errore" : e.status === "deferred" ? "Rinviata" : e.status === "cancelled" ? "Annullata" : "In corso"}</span></div>
                <div className="text-xs text-slate-500">{e.milestone.startsWith("overdue") ? "Promemoria scaduto" : `Soglia ${e.milestone.slice(1)} giorni`} · {fmtDT(e.sent_at || e.last_attempt_at || e.created_at)}{e.attempts ? ` · tentativi ${e.attempts}` : ""}</div>
                {e.error && <div className="text-xs text-rose-300 mt-1">Motivo: {e.error}</div>}
              </div>
            ))}
            {data.previous_alerts.length > 0 && <div className="pt-2 text-xs text-slate-400">Scadenze precedenti: {data.previous_alerts.map(p => `${fmtD(p.expiry_date)} (${p.closed_reason || p.status})`).join(", ")}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SettingsCard({ settings, onSave, isAdmin, onRun, running, lastRun }) {
  const set = (k, v) => onSave({ ...settings, [k]: v });
  return (
    <div className="rounded-2xl border border-slate-800 bg-[#0C0B09] p-4 grid gap-4 md:grid-cols-[repeat(4,auto)_1fr] md:items-center text-sm" data-testid="expiry-settings">
      <label className="flex items-center gap-2 text-slate-300"><Switch checked={settings.enabled} onCheckedChange={v => set("enabled", v)} data-testid="expiry-setting-enabled" />Avvisi attivi</label>
      <label className="flex items-center gap-2 text-slate-300"><Switch checked={settings.email_enabled} onCheckedChange={v => set("email_enabled", v)} data-testid="expiry-setting-email" />Email</label>
      <label className="flex items-center gap-2 text-slate-300"><Switch checked={settings.notify_driver} onCheckedChange={v => set("notify_driver", v)} data-testid="expiry-setting-driver" />Avvisa l'autista</label>
      <div className="flex items-center gap-2 text-slate-300">Promemoria scaduti ogni
        <Select value={String(settings.overdue_every_days)} onValueChange={v => set("overdue_every_days", Number(v))}>
          <SelectTrigger className="w-28 h-8" data-testid="expiry-setting-frequency"><SelectValue /></SelectTrigger>
          <SelectContent>{[1, 2, 3, 7].map(n => <SelectItem key={n} value={String(n)}>{n === 1 ? "1 giorno" : `${n} giorni`}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3 md:justify-end text-xs text-slate-500">
        <span data-testid="expiry-last-run">Ultimo controllo automatico: {lastRun ? fmtDT(lastRun.finished_at) : "mai"}</span>
        {isAdmin && <button type="button" disabled={running} onClick={onRun} data-testid="expiry-run-now" className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 border border-amber-500/40 text-amber-200 disabled:opacity-50"><Play size={13} />{running ? "Controllo..." : "Esegui ora"}</button>}
      </div>
    </div>
  );
}

const ALL = "all";

export default function Scadenze() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [fKind, setFKind] = useState(ALL);
  const [fType, setFType] = useState(ALL);
  const [fState, setFState] = useState(ALL);
  const [historyId, setHistoryId] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { setData((await api.get("/expiry-alerts")).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const items = useMemo(() => data?.items || [], [data]);
  const types = useMemo(() => [...new Set(items.map(i => i.doc_type))].sort(), [items]);
  const filtered = useMemo(() => items.filter(i => {
    const s = q.trim().toLowerCase();
    if (s && ![i.entity_name, i.plate, i.doc_type].filter(Boolean).some(x => x.toLowerCase().includes(s))) return false;
    if (fKind !== ALL && i.kind !== fKind) return false;
    if (fType !== ALL && i.doc_type !== fType) return false;
    if (fState === "ack") return i.alert_status === "acknowledged";
    if (fState === "within_7") return i.days >= 0 && i.days <= 7;
    if (fState !== ALL && i.severity !== fState) return false;
    return true;
  }), [items, q, fKind, fType, fState]);

  const ack = async (it) => {
    try { await api.patch(`/expiry-alerts/${it.alert_id}/ack`); toast.success("Avviso preso in carico"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveSettings = async (cfg) => {
    try { const r = await api.put("/expiry-alerts/settings", cfg); setData(d => ({ ...d, settings: r.data })); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const r = (await api.post("/tasks/run-expiration-check")).data;
      toast.success(`Controllo completato: ${r.notified} nuovi avvisi, ${r.email_sent} email inviate${r.email_error ? `, ${r.email_error} errori` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setRunning(false);
  };

  if (!data) return <div className="text-slate-500 text-sm p-10 text-center">Caricamento...</div>;
  return (
    <div className="space-y-5" data-testid="scadenze-page">
      <div>
        <h1 className="font-display text-3xl text-white flex items-center gap-2"><CalendarClock className="text-amber-400" />Scadenze e notifiche</h1>
        <p className="text-sm text-slate-400 mt-1">Documenti di flotta e autisti monitorati ogni giorno alle 08:00 (ora di Roma), con notifica interna ed email.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Counter icon={CalendarClock} label="Entro 30 giorni" value={data.counts.within_30} tone="text-yellow-200" testid="count-within-30" />
        <Counter icon={AlertTriangle} label="Entro 7 giorni" value={data.counts.within_7} tone="text-amber-300" testid="count-within-7" />
        <Counter icon={AlertTriangle} label="Scaduti" value={data.counts.expired} tone="text-rose-300" testid="count-expired" />
        <Counter icon={BellRing} label="Notifiche non lette" value={data.unread} tone="text-amber-200" testid="count-unread" />
      </div>
      <SettingsCard settings={data.settings} onSave={saveSettings} isAdmin={["admin", "admin_azienda"].includes(user?.role)} onRun={runNow} running={running} lastRun={data.last_run} />
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Input placeholder="Cerca veicolo, targa o autista" value={q} onChange={e => setQ(e.target.value)} data-testid="expiry-filter-search" />
        <Select value={fKind} onValueChange={setFKind}><SelectTrigger data-testid="expiry-filter-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Veicoli e autisti</SelectItem><SelectItem value="vehicle">Solo veicoli</SelectItem><SelectItem value="driver">Solo autisti</SelectItem></SelectContent></Select>
        <Select value={fType} onValueChange={setFType}><SelectTrigger data-testid="expiry-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutte le tipologie</SelectItem>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        <Select value={fState} onValueChange={setFState}><SelectTrigger data-testid="expiry-filter-state"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutti gli stati</SelectItem><SelectItem value="in_scadenza">In scadenza</SelectItem><SelectItem value="within_7">Entro 7 giorni</SelectItem>
            <SelectItem value="imminente">Scadenza imminente</SelectItem><SelectItem value="scaduto">Scaduti</SelectItem><SelectItem value="ack">Presi in carico</SelectItem></SelectContent></Select>
      </div>
      <div className="space-y-2">
        {filtered.length === 0 && <div className="text-sm text-slate-500 text-center py-10" data-testid="expiry-empty">Nessun documento in scadenza o scaduto</div>}
        {filtered.map(it => <AlertRow key={it.alert_id} it={it} onOpen={i => nav(i.link)} onAck={ack} onHistory={i => setHistoryId(i.alert_id)} />)}
      </div>
      {historyId && <HistoryDialog alertId={historyId} onClose={() => setHistoryId(null)} />}
    </div>
  );
}
